import { Warning } from 'assets/icons';
import PanelCard from 'components/card/PanelCard';
import Tooltip from 'components/tooltips/Tooltips';
import {
  remunerationSections,
  restaurantTicketLines,
} from 'helper/remunerationConditions';

/**
 * The company's remuneration terms, read-only (JBWEB-1208).
 *
 * The contract preview shows what the contract was drawn from, so nothing here
 * opens an editor: the panel shows one modal at a time, and a pencil that
 * could not open anything would be worse than no pencil.
 *
 * Same cards as the company panel — PanelCard, folded from the same control —
 * and same sentences, from helper/remunerationConditions: the same settings
 * described twice in two wordings would drift.
 */
const RemunerationSummary = ({ companyInfo }) => {
  const sections = remunerationSections(companyInfo);
  const bonuses = companyInfo?.bonusesAndAllowances?.bonuses ?? [];
  const ticketLines = restaurantTicketLines(companyInfo);

  return (
    <div className={'flex flex-col gap-3'}>
      <PanelCard
        title={'Conditions de rémunération'}
        collapsible
        contentClassName={'divide-y'}
      >
        {sections.map((section) => (
          <div key={section.key} className={'py-3 px-4 flex justify-between gap-2'}>
            <div className={'min-w-0'}>
              <div className={'text-gray-900'}>{section.title}</div>
              {section.intro && (
                <div className={'text-gray-500'}>{section.intro}</div>
              )}
              {section.bullets.length > 0 && (
                <ul className={'list-disc list-inside text-gray-500'}>
                  {section.bullets.map((line, index) => (
                    <li key={index}>{line}</li>
                  ))}
                </ul>
              )}
              {section.extra && (
                <div className={'text-gray-500'}>{section.extra}</div>
              )}
            </div>
            {/* Same warning the company panel shows, and for the same reason:
                a section still on its defaults was never agreed with this
                client, which is worth knowing while reading their contract. */}
            {section.isDefault && (
              <Tooltip text={'Données par défaut'} placement={'top'} zIndex={60}>
                <span className={'my-auto shrink-0 cursor-help'}>
                  <Warning wh={20} color={'#975612'} />
                </span>
              </Tooltip>
            )}
          </div>
        ))}
      </PanelCard>

      <PanelCard
        title={'Ticket restaurant'}
        collapsible
        contentClassName={'py-3 px-4 text-gray-500'}
      >
        {ticketLines.length > 0 ? (
          ticketLines.map((line, index) => <div key={index}>{line}</div>)
        ) : (
          'Aucun ticket restaurant.'
        )}
      </PanelCard>

      <PanelCard
        title={'Primes & indemnités'}
        collapsible
        defaultCollapsed
        contentClassName={'divide-y'}
      >
        {bonuses.length === 0 ? (
          <div className={'py-3 px-4 text-gray-500'}>
            Aucune prime ni indemnité.
          </div>
        ) : (
          bonuses.map((bonus, index) => (
            <div
              key={bonus._id ?? index}
              className={'py-3 px-4 flex justify-between gap-2'}
            >
              <span className={'text-gray-900 min-w-0 truncate'}>
                {bonus.name?.name?.trim() ?? bonus.name ?? '—'}
              </span>
              <span className={'text-gray-500 shrink-0'}>
                {bonus.unit?.name ?? bonus.unit ?? ''}
                {bonus.payed !== undefined && bonus.payed !== null
                  ? ` · ${bonus.payed}`
                  : ''}
              </span>
            </div>
          ))
        )}
      </PanelCard>
    </div>
  );
};

export default RemunerationSummary;
